/**
 * Where the app lives, and what belongs to it.
 *
 * The messenger is mounted under `/app` so that `/` can be a page for people
 * who have not signed in. One constant, imported by the entry point, the
 * router basename and the landing page, so the three can never disagree.
 */

export const APP_BASE = '/app';

/** The flag the landing reads to redirect a returning visitor before paint. */
export const SIGNED_IN_HINT = 'echo.signed-in';

/**
 * Marks the hop that flag causes, so the app can tell two arrivals apart.
 *
 * Somebody who pressed "Open Echo" and has no session should meet the sign-in
 * screen — that is what they asked for. Somebody who typed the bare domain and
 * was forwarded here on a stale flag asked for nothing of the kind, and belongs
 * back on the landing. Same URL, different intent; this is the difference.
 */
export const HINT_HOP = 'via';

/**
 * The first path segments the app answers to.
 *
 * These existed at the root before the move, and invite links in particular
 * were sent to other people and are still being clicked. A request for one of
 * them at the root is forwarded under `/app` rather than shown the landing —
 * otherwise every `/join/<code>` ever shared quietly turns into a sales page.
 *
 * `login` is not a route in `App`; it is where the OAuth flow sends a visitor
 * who is not signed in (see `routes/oauth.ts`), and any path reaches the
 * sign-in screen while signed out. It has to be listed all the same, or
 * "Sign in with Echo" lands on the landing page and the flow stops there.
 */
const APP_SEGMENTS = [
  'chat',
  'join',
  'settings',
  'new',
  'ai',
  'calls',
  'contacts',
  'guild',
  'login',
];

/** True while the browser is already somewhere inside the app. */
export function isAppPath(pathname: string): boolean {
  return pathname === APP_BASE || pathname.startsWith(`${APP_BASE}/`);
}

/**
 * The `/app` path a legacy root URL should become, or null if the root URL is
 * the landing page and should stay where it is.
 */
export function legacyAppPath(pathname: string): string | null {
  const segment = pathname.split('/')[1] ?? '';
  return APP_SEGMENTS.includes(segment) ? `${APP_BASE}${pathname}` : null;
}
